<template>
    <div>
        <hts-router-drawer
            :title="$t('labels.viewInvoicingInformation')"
            size="large">
            <div class="mb-3 text-right">
                <hts-form-button
                    v-if="checkPermission(layoutPermissions.partner_invoicing_information_add)"
                    color="main"
                    icon-name="hi-plus-circle-icon"
                    icon-position="right"
                    @click="openInvoicingInformation">
                    {{ $t('labels.add') }}
                </hts-form-button>
            </div>

            <hts-data-table
                ref="dataTables"
                :headers="cooperatorInvoicingInformation"
                :server="server"
                store-name="cooperatorDataTable"
                store-key="CooperatorInvoicingInformation"
                :use-archive="useArchive"
                use-selection />

            <AddInvoicingInformation
                ref="addInvoicingInformation"
                @refreshTables="refreshTables" />

            <hts-modal
                :show="showPopupDeleteInvoicingInformation"
                :title="$t('labels.deleteInvoicingInformation')"
                type="confirm"
                :content="$t('labels.confirmDeleteInvoicingInformation')"
                @hide="closePopupDeleteInvoicingInformation"
                @confirm="handleDeleteInvoicingInformation" />
        </hts-router-drawer>

        <router-view @refresh-tables="refreshTables" />
    </div>
</template>

<script setup lang="ts">
import AddInvoicingInformation from './invoicing-information/-components/add.vue'
import { checkPermission } from '@/utils/permission-check'
import layoutPermissions from '~/data/permissions/layout-permissions'
import actions from '~/pages/trucker-[id]/partner/-components/actions.vue'

const { $formatters } = useNuxtApp()

const visible = ref(false)
const dataTables = ref()
const cooperatorInvoicingInformationId = ref('')
const addInvoicingInformation: any = ref(null)
const showPopupDeleteInvoicingInformation: any = ref(false)

const cooperatorCode = computed(() => useRoute().params.idpartner)

const server = {
  method: 'post',
  url: '/partner/invoicing-information/' + cooperatorCode.value + '/list'
}

const refreshTables = () => {
  if (dataTables.value) {
    dataTables.value.refresh()
  }
}

const openInvoicingInformation = () => {
  if (addInvoicingInformation.value) {
    addInvoicingInformation.value.open(cooperatorCode.value)
  }
}

const openEditInvoicingInformation = (item: any) => {
  useRouter().push({
    name: 'trucker-id-partner-idpartner-invoicing-information-idinvoicinginformation-edit',
    params: { idinvoicinginformation: item._id }
  })
}

const openDeleteInvoicingInformation = (item: any) => {
  cooperatorInvoicingInformationId.value = item?._id
  showPopupDeleteInvoicingInformation.value = true
}

const closePopupDeleteInvoicingInformation = () => {
  showPopupDeleteInvoicingInformation.value = false
}

const cooperatorInvoicingInformation = [
  {
    field: 'companyName',
    headerName: $t('labels.companyName'),
    filter: 'agTextColumnFilter'
  },
  {
    field: 'phone',
    headerName: $t('labels.phone'),
    filter: 'agTextColumnFilter'
  },
  {
    field: 'address',
    headerName: $t('labels.address'),
    filter: 'agTextColumnFilter'
  },
  {
    field: 'email',
    headerName: $t('labels.email'),
    filter: 'agTextColumnFilter'
  },
  {
    field: 'taxNumber',
    headerName: $t('labels.taxNumber'),
    filter: 'agTextColumnFilter'
  },
  {
    field: 'contactName',
    headerName: $t('labels.contactName'),
    filter: 'agTextColumnFilter'
  },
  {
    field: 'createdAt',
    headerName: $t('labels.createdAt'),
    sortable: true,
    filter: 'agDateColumnFilter',
    valueGetter: (params: any) => $formatters.datetime(params.data.createdAt)
  },
  {
    field: 'updatedAt',
    headerName: $t('labels.updatedAt'),
    filter: 'agDateColumnFilter',
    sortable: true,
    valueGetter: (params: any) => $formatters.datetime(params.data.updatedAt)
  },
  {
    field: 'action',
    pinned: 'right',
    sortable: false,
    lockPosition: true,
    lockPinned: true,
    headerName: $t('labels.action'),
    cellStyle: { overflow: 'visible' },
    cellRenderer: actions,
    cellRendererParams: {
      storeKey: 'cooperatorDataTable',
      storeName: 'CooperatorInvoicingInformation',
      items: [
        {
          icon: 'hi-pencil-square-icon',
          label: $t('labels.edit'),
          class: 'text-blue-600 hover:text-blue-500',
          permissions: checkPermission(layoutPermissions.partner_invoicing_information_edit),
          click: openEditInvoicingInformation
        },
        {
          icon: 'hi-trash-icon',
          label: $t('labels.delete'),
          class: 'text-error-600 hover:text-error-500',
          permissions: checkPermission(layoutPermissions.partner_invoicing_information_delete),
          click: openDeleteInvoicingInformation,
          isArchive: true
        }
      ]
    }
  }
]

const handleDeleteInvoicingInformation = () => {
  const api = {
    url: '/partner/invoicing-information/cooperatorId/delete/cooperatorInvoicingInformationId',
    method: 'delete'
  }
  $api(api, {
    uriParams: {
      cooperatorId: cooperatorCode.value,
      cooperatorInvoicingInformationId: cooperatorInvoicingInformationId.value
    }
  })
    .then((data: any) => {
      if (!data) {
        return true
      }
      const responseData: any = data.data.value
      if (responseData.success) {
        process.client && $toast().success($t('labels.deleteInvoicingInformationSuccess'))
      }
      refreshTables()
      showPopupDeleteInvoicingInformation.value = false
    })
    .catch(() => {
      showPopupDeleteInvoicingInformation.value = false
    })
}

const close = () => {
  visible.value = false
}

const useArchive = {
  api: $url.partner.archiveInvoicingInformation,
  permissions: checkPermission(layoutPermissions.partner_invoicing_information_archive)
}
</script>
